import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import OrgTree from 'react-org-tree';

const MyTeamTab = () => {
  const { user, getAuthHeaders } = useAuth();
  const [teamData, setTeamData] = useState(null);
  const [departmentMembers, setDepartmentMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentLoading, setDepartmentLoading] = useState(true);
  const [error, setError] = useState('');
  const [itOnboardingData, setItOnboardingData] = useState([]);
  const [showDepartmentTree, setShowDepartmentTree] = useState(false);
  const [departmentTreeData, setDepartmentTreeData] = useState(null);
  const [departmentHead, setDepartmentHead] = useState(null);
  const [departmentTreeLoading, setDepartmentTreeLoading] = useState(false);
  const [orgTreeData, setOrgTreeData] = useState(null);
  const [orgTreeLoading, setOrgTreeLoading] = useState(false);
  const [selectedTreeOption, setSelectedTreeOption] = useState('my_chain'); // 'my_chain' or department name
  const [departments, setDepartments] = useState([]);
  const [deptOptionsLoading, setDeptOptionsLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchTeamData();
      fetchDepartmentMembers();
      fetchITOnboardingData();
      fetchDepartmentsList();
    } else {
      setLoading(false);
      setDepartmentLoading(false);
    }
  }, [user]);

  // Load organization tree data when team data is available
  useEffect(() => {
    const loadOrgTreeData = async () => {
      if (teamData && !showDepartmentTree) {
        setOrgTreeLoading(true);
        try {
          const treeData = await transformDataForOrgTree(teamData);
          setOrgTreeData(treeData);
        } catch (error) {
          console.error('Error loading organization tree:', error);
          setOrgTreeData(null);
        } finally {
          setOrgTreeLoading(false);
        }
      }
    };

    loadOrgTreeData();
  }, [teamData, showDepartmentTree]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}/user-hierarchy/user-hierarchy/${user.id}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Team data received:', data);
        setTeamData(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch hierarchy:', errorText);
        setError('Failed to load team information. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError(`Failed to load team information: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentMembers = async () => {
    try {
      setDepartmentLoading(true);
      if (!user?.department) {
        setDepartmentMembers([]);
        return;
      }

      const response = await fetch(`${API_URL}/users/department/${encodeURIComponent(user.department)}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out current user from department members (check both _id and id)
        const filteredMembers = data.filter(member => 
          member._id !== user.id && 
          member._id !== user._id && 
          member.id !== user.id && 
          member.id !== user._id
        );
        setDepartmentMembers(filteredMembers);
      } else {
        console.error('Failed to fetch department members');
        setDepartmentMembers([]);
      }
    } catch (err) {
      console.error('Error fetching department members:', err);
      setDepartmentMembers([]);
    } finally {
      setDepartmentLoading(false);
    }
  };

  const fetchITOnboardingData = async () => {
    try {
      console.log('Fetching IT onboarding data...');
      const response = await fetch(`${API_URL}/it-onboarding/it-forms`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('IT onboarding forms data:', data);
        setItOnboardingData(data.data || []);
      } else {
        console.error('Failed to fetch IT onboarding data');
        setItOnboardingData([]);
      }
    } catch (error) {
      console.error('Error fetching IT onboarding data:', error);
      setItOnboardingData([]);
    }
  };

  // Fetch department head from department master
  const fetchDepartmentHead = async () => {
    try {
      console.log('Fetching department head...');
      const response = await fetch(`${API_URL}/api/departments`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Departments data:', data);
        
        // Find the department that matches the current user's department
        const userDepartment = user.department || 'IT';
        const department = data.find(dept => 
          dept.name === userDepartment || 
          dept.departmentName === userDepartment
        );
        
        if (department && department.head) {
          console.log('Department head found:', department.head);
          setDepartmentHead(department.head);
        } else {
          console.log('No department head found for department:', userDepartment);
          setDepartmentHead(null);
        }
      } else {
        console.error('Failed to fetch departments');
        setDepartmentHead(null);
      }
    } catch (error) {
      console.error('Error fetching department head:', error);
      setDepartmentHead(null);
    }
  };

  // Fetch department hierarchy from database
  const fetchDepartmentHierarchy = async (departmentNameParam) => {
    try {
      console.log('Fetching department hierarchy...');
      const userDepartment = departmentNameParam || user.department || 'IT';
      console.log('User department:', userDepartment);
      
      const response = await fetch(`${API_URL}/user-hierarchy/department-hierarchy?department=${encodeURIComponent(userDepartment)}`, {
        headers: getAuthHeaders()
      });
      
      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Department hierarchy data:', data);
        console.log('Number of employees:', data.employees ? data.employees.length : 0);
        
        if (data.employees && data.employees.length > 0) {
          console.log('Sample employee:', data.employees[0]);
          console.log('Employees with manager_id null:', data.employees.filter(emp => emp.manager_id === null).length);
        }
        
        return data;
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch department hierarchy. Status:', response.status, 'Error:', errorText);
        return null;
      }
    } catch (error) {
      console.error('Error fetching department hierarchy:', error);
      return null;
    }
  };

  // Build recursive department tree structure based on actual manager-employee relationships
  const buildDepartmentHierarchy = (employees, managerId = null) => {
    console.log('Building hierarchy for managerId:', managerId);
    console.log('Total employees:', employees.length);
    
    // Find employees who report to this manager
    const subordinates = employees.filter(emp => {
      // Handle both string and ObjectId comparisons
      const empManagerId = emp.manager_id || emp.managerId;
      const targetManagerId = managerId;
      
      // Convert both to strings for comparison
      const empManagerStr = empManagerId ? empManagerId.toString() : null;
      const targetManagerStr = targetManagerId ? targetManagerId.toString() : null;
      
      return empManagerStr === targetManagerStr;
    });
    
    console.log('Subordinates found for managerId', managerId, ':', subordinates.length);
    console.log('Subordinates:', subordinates.map(s => ({ 
      name: s.fullName || s.firstName + ' ' + s.lastName, 
      manager_id: s.manager_id || s.managerId,
      id: s.id || s._id
    })));
    
    if (subordinates.length === 0) {
      console.log('No subordinates found, returning empty array');
      return [];
    }
    
    // Recursively build children for each subordinate
    return subordinates.map(employee => {
      const employeeId = employee.id || employee._id;
      const children = buildDepartmentHierarchy(employees, employeeId);
      console.log('Built children for', employee.fullName || employee.firstName + ' ' + employee.lastName, ':', children.length);
      
      return createEmployeeNode({
        ...employee,
        name: employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown',
        role: managerId === null ? 'Department Head' : 'Employee',
        level: getEmployeeLevelFromITForm(employee.email) || getEmployeeLevel(employee.employeeGrade || employee.designation || employee.title)
      }, children);
    });
  };

  // Create department tree structure with proper hierarchy
  const createDepartmentTree = async (departmentNameParam) => {
    console.log('Creating department tree with proper hierarchy');
    
    try {
      // Fetch hierarchy data from database
      const hierarchyData = await fetchDepartmentHierarchy(departmentNameParam);
      
      if (!hierarchyData || !hierarchyData.employees || hierarchyData.employees.length === 0) {
        console.log('No hierarchy data available, creating fallback structure from team data');
        
        // Fallback: Use existing team data structure which has correct reporting relationships
        if (teamData && (teamData.user || teamData.teamMembers || departmentMembers)) {
          console.log('Creating hierarchy from existing team data with correct reporting relationships');
          
          // Use the existing organization tree data if available
          if (orgTreeData) {
            console.log('Using existing organization tree structure');
            return orgTreeData;
          }
          
          // If that fails, create a simple flat structure
          console.log('Creating simple flat hierarchy');
          
          // Find the highest level employee to be department head
          const allMembers = [];
          
          // Add current user
          allMembers.push({
            ...teamData.user,
            name: teamData.user.name || `${teamData.user.firstName || ''} ${teamData.user.lastName || ''}`.trim() || 'Unknown',
            level: getEmployeeLevelFromITForm(teamData.user.email) || getEmployeeLevel(teamData.user.employeeGrade || teamData.user.designation || teamData.user.title)
          });
          
          // Add team members
          if (teamData.teamMembers && teamData.teamMembers.length > 0) {
            allMembers.push(...teamData.teamMembers.map(member => ({
              ...member,
              name: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
              level: getEmployeeLevelFromITForm(member.email) || getEmployeeLevel(member.employeeGrade || member.designation || member.title)
            })));
          }
          
          // Add department members
          if (departmentMembers && departmentMembers.length > 0) {
            allMembers.push(...departmentMembers.map(member => ({
              ...member,
              name: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
              level: getEmployeeLevelFromITForm(member.email) || getEmployeeLevel(member.employeeGrade || member.designation || member.title)
            })));
          }
          
          // Remove duplicates and sort by level
          const uniqueMembers = allMembers.filter((member, index, self) => 
            index === self.findIndex(m => m._id === member._id)
          );
          
          uniqueMembers.sort((a, b) => b.level - a.level);
          
          // Create department head (highest level)
          const departmentHead = uniqueMembers[0];
          const otherMembers = uniqueMembers.slice(1);
          
          const departmentHeadNode = createEmployeeNode({
            ...departmentHead,
            role: 'Department Head'
          }, []);
          
          // Add all others as direct reports (flat structure)
          departmentHeadNode.children = otherMembers.map(member => createEmployeeNode({
            ...member,
            role: 'Employee'
          }, []));
          
          console.log('Simple flat hierarchy created');
          return departmentHeadNode;
        }
        
        return null;
      }
      
      // Build the tree starting from department head (manager_id = null)
      const departmentTree = buildDepartmentHierarchy(hierarchyData.employees, null);
      
      console.log('Department tree built:', departmentTree.length, 'root nodes');
      
      if (departmentTree.length === 0) {
        console.log('No department head found (no employees with manager_id = null)');
        
        // If no department head, find the highest level employee and make them the root
        const sortedEmployees = hierarchyData.employees.sort((a, b) => {
          const levelA = getEmployeeLevelFromITForm(a.email) || getEmployeeLevel(a.employeeGrade || a.designation || a.title);
          const levelB = getEmployeeLevelFromITForm(b.email) || getEmployeeLevel(b.employeeGrade || b.designation || b.title);
          return levelB - levelA;
        });
        
        if (sortedEmployees.length > 0) {
          const highestLevelEmployee = sortedEmployees[0];
          console.log('Using highest level employee as department head:', highestLevelEmployee.fullName);
          
          const departmentHeadNode = createEmployeeNode({
            ...highestLevelEmployee,
            name: highestLevelEmployee.fullName || `${highestLevelEmployee.firstName || ''} ${highestLevelEmployee.lastName || ''}`.trim() || 'Unknown',
            role: 'Department Head',
            level: getEmployeeLevelFromITForm(highestLevelEmployee.email) || getEmployeeLevel(highestLevelEmployee.employeeGrade || highestLevelEmployee.designation || highestLevelEmployee.title)
          }, []);
          
          // Build hierarchy starting from this employee
          const employeeId = highestLevelEmployee.id || highestLevelEmployee._id;
          departmentHeadNode.children = buildDepartmentHierarchy(hierarchyData.employees, employeeId);
          
          return departmentHeadNode;
        }
        
        return null;
      }
      
      // If multiple department heads, take the first one
      return departmentTree[0];
    } catch (error) {
      console.error('Error creating department tree:', error);
      return null;
    }
  };

  // Fetch departments for dropdown
  const fetchDepartmentsList = async () => {
    try {
      setDeptOptionsLoading(true);
      const res = await fetch(`${API_URL}/departments/public`);
      const data = await res.json();
      const names = Array.isArray(data) ? data.map(d => d.departmentName).filter(Boolean) : [];
      setDepartments(names);
    } catch (e) {
      console.error('Failed to load departments list', e);
      setDepartments([]);
    } finally {
      setDeptOptionsLoading(false);
    }
  };

  // React to dropdown selection changes
  useEffect(() => {
    const run = async () => {
      const isDept = selectedTreeOption !== 'my_chain';
      setShowDepartmentTree(isDept);
      if (isDept) {
        setDepartmentTreeLoading(true);
        try {
          const treeData = await createDepartmentTree(selectedTreeOption);
          setDepartmentTreeData(treeData);
        } catch (e) {
          console.error('Error loading selected department tree', e);
          setDepartmentTreeData(null);
        } finally {
          setDepartmentTreeLoading(false);
        }
      } else {
        // back to reporting chain
        setDepartmentTreeData(null);
      }
    };
    run();
  }, [selectedTreeOption]);

  // Helper function to get actual employee level from IT onboarding form data
  const getEmployeeLevelFromITForm = (employeeEmail) => {
    console.log('Looking for IT onboarding data for:', employeeEmail);
    console.log('Available IT onboarding data:', itOnboardingData);
    
    // Find the employee's IT onboarding form by email
    const employeeForm = itOnboardingData.find(form => 
      form.email?.toLowerCase() === employeeEmail?.toLowerCase()
    );
    
    if (employeeForm && employeeForm.employeeGrade) {
      const gradeMatch = employeeForm.employeeGrade.toString().match(/L(\d+)/i);
      if (gradeMatch) {
        const level = parseInt(gradeMatch[1]);
        console.log('Found actual level from IT form:', level, 'for', employeeEmail);
        return level;
      }
    }
    
    console.log('No IT onboarding data found for:', employeeEmail, 'defaulting to L6');
    return 6; // Default level if no IT form data found
  };

  // Helper function to determine employee level from grade or title (fallback)
  const getEmployeeLevel = (gradeOrTitle) => {
    if (!gradeOrTitle) {
      console.log('No grade or title provided, defaulting to L6 (mid-level employee)');
      return 6; // Default to mid-level instead of L1
    }
    
    console.log('Analyzing grade/title:', gradeOrTitle);
    
    // Check for employee grade (L1, L2, etc.) - L1 is lowest, L10 is highest
    const gradeMatch = gradeOrTitle.toString().match(/L(\d+)/i);
    if (gradeMatch) {
      const level = parseInt(gradeMatch[1]);
      console.log('Found grade level:', level, 'from input:', gradeOrTitle);
      return level;
    }
    
    console.log('No specific level detected, defaulting to L6 (mid-level employee)');
    return 6; // Default to mid-level instead of L1
  };

  // Build recursive employee hierarchy with no depth limit
  const buildEmployeeHierarchy = (employees, managerId) => {
    console.log('Building hierarchy for managerId:', managerId);
    
    const subordinates = employees.filter(emp => {
      const empManagerId = emp.manager_id || emp.managerId;
      return empManagerId && empManagerId.toString() === managerId?.toString();
    });
    
    console.log('Found subordinates:', subordinates.length);
    
    return subordinates.map(employee => {
      const employeeId = employee.id || employee._id;
      const children = buildEmployeeHierarchy(employees, employeeId);
      console.log('Built children for', employee.fullName || employee.firstName + ' ' + employee.lastName, ':', children.length);
      
      return createEmployeeNode({
        ...employee,
        name: employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown',
        role: managerId === null ? 'Department Head' : 'Employee',
        level: getEmployeeLevelFromITForm(employee.email) || getEmployeeLevel(employee.employeeGrade || employee.designation || employee.title)
      }, children);
    });
  };

  // Build employee-specific hierarchy tree (manager above + subordinates below)
  const buildEmployeeSpecificHierarchy = (employees, currentEmployeeId) => {
    console.log('Building employee-specific hierarchy for:', currentEmployeeId);
    
    // Find the current employee
    const currentEmployee = employees.find(emp => {
      const empId = emp.id || emp._id;
      return empId && empId.toString() === currentEmployeeId?.toString();
    });
    
    if (!currentEmployee) {
      console.log('Current employee not found in hierarchy data');
      return null;
    }
    
    console.log('Found current employee:', currentEmployee.fullName || currentEmployee.firstName + ' ' + currentEmployee.lastName);
    
    // Find direct subordinates (employees who report to current employee)
    const subordinates = employees.filter(emp => {
      const empManagerId = emp.manager_id || emp.managerId;
      const currentEmpId = currentEmployee.id || currentEmployee._id;
      return empManagerId && empManagerId.toString() === currentEmpId?.toString();
    });
    
    console.log('Found subordinates:', subordinates.length);
    
    // Recursively build subordinate hierarchy
    const subordinateNodes = subordinates.map(subordinate => {
      const subordinateId = subordinate.id || subordinate._id;
      const children = buildEmployeeSpecificHierarchy(employees, subordinateId);
      
      return createEmployeeNode({
        ...subordinate,
        name: subordinate.fullName || `${subordinate.firstName || ''} ${subordinate.lastName || ''}`.trim() || 'Unknown',
        role: 'Subordinate',
        level: getEmployeeLevelFromITForm(subordinate.email) || getEmployeeLevel(subordinate.employeeGrade || subordinate.designation || subordinate.title)
      }, children || []);
    });
    
    // Create current employee node with subordinates
    const currentEmployeeNode = createEmployeeNode({
      ...currentEmployee,
      name: currentEmployee.fullName || `${currentEmployee.firstName || ''} ${currentEmployee.lastName || ''}`.trim() || 'Unknown',
      role: 'Current User',
      level: getEmployeeLevelFromITForm(currentEmployee.email) || getEmployeeLevel(currentEmployee.employeeGrade || currentEmployee.designation || currentEmployee.title)
    }, subordinateNodes);
    
    // Find and add manager above (if exists)
    const managerId = currentEmployee.manager_id || currentEmployee.managerId;
    if (managerId) {
      const manager = employees.find(emp => {
        const empId = emp.id || emp._id;
        return empId && empId.toString() === managerId?.toString();
      });
      
      if (manager) {
        console.log('Found manager:', manager.fullName || manager.firstName + ' ' + manager.lastName);
        
        const managerNode = createEmployeeNode({
          ...manager,
          name: manager.fullName || `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || 'Unknown',
          role: 'Manager',
          level: getEmployeeLevelFromITForm(manager.email) || getEmployeeLevel(manager.employeeGrade || manager.designation || manager.title)
        }, [currentEmployeeNode]);
        
        return managerNode;
      }
    }
    
    // If no manager, current employee is the root
    return currentEmployeeNode;
  };

  // Transform data for react-org-tree with employee-specific hierarchy
  const transformDataForOrgTree = async (data) => {
    if (!data) return null;
    
    console.log('Creating employee-specific hierarchy tree');
    
    try {
      // Fetch complete hierarchy data from database
      const hierarchyData = await fetchDepartmentHierarchy();
      
      if (hierarchyData && hierarchyData.employees && hierarchyData.employees.length > 0) {
        console.log('Using database hierarchy data for employee-specific tree');
        
        // Get current user ID
        const currentUserId = data.user?.id || data.user?._id;
        console.log('Current user ID:', currentUserId);
        
        if (currentUserId) {
          // Build employee-specific hierarchy (manager above + subordinates below)
          const employeeSpecificTree = buildEmployeeSpecificHierarchy(hierarchyData.employees, currentUserId);
          
          if (employeeSpecificTree) {
            console.log('Employee-specific tree built successfully');
            return employeeSpecificTree;
          } else {
            console.log('Could not build employee-specific tree, falling back to limited structure');
          }
        }
      }
      
      // Fallback: Use existing limited structure if database data not available
      console.log('No database hierarchy data, using fallback structure');
      
      // Create current user node
      const currentUserNode = createEmployeeNode({
        ...data.user,
        name: data.user.name || `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim() || 'Unknown',
        role: 'Current User',
        level: getEmployeeLevelFromITForm(data.user.email) || getEmployeeLevel(data.user.employeeGrade || data.user.designation || data.user.title)
      }, []);
      
      // Add direct reports as children of current user
      if (data.teamMembers && data.teamMembers.length > 0) {
        console.log('Adding direct reports to current user:', data.teamMembers);
        const directReports = data.teamMembers.map(member => createEmployeeNode({
          ...member,
          name: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
          role: 'Direct Report',
          level: getEmployeeLevelFromITForm(member.email) || getEmployeeLevel(member.employeeGrade || member.designation || member.title)
        }, []));
        
        currentUserNode.children = directReports;
      }
      
      // If manager exists, make manager the root and current user as child
      if (data.managersAbove && data.managersAbove.length > 0) {
        console.log('Adding manager above current user:', data.managersAbove);
        const managerNode = createEmployeeNode({
          ...data.managersAbove[0], // Take the first manager
          name: data.managersAbove[0].name || 'Unknown Manager',
          role: 'Reporting Manager',
          level: getEmployeeLevelFromITForm(data.managersAbove[0].email) || getEmployeeLevel(data.managersAbove[0].title || 'Manager')
        }, [currentUserNode]);
        
        return managerNode;
      }
      
      // If no manager, current user is the root
      return currentUserNode;
      
    } catch (error) {
      console.error('Error creating recursive employee hierarchy:', error);
      
      // Return fallback structure on error
      const currentUserNode = createEmployeeNode({
        ...data.user,
        name: data.user.name || `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim() || 'Unknown',
        role: 'Current User',
        level: getEmployeeLevelFromITForm(data.user.email) || getEmployeeLevel(data.user.employeeGrade || data.user.designation || data.user.title)
      }, []);
      
      return currentUserNode;
    }
  };
  
  // Helper function to get color based on employee level
  const getLevelColor = (level) => {
    const levelColors = {
      1: '#f44336',  // Red - L1 (lowest)
      2: '#ff9800',  // Orange - L2
      3: '#ffc107',  // Amber - L3
      4: '#8bc34a',  // Light Green - L4
      5: '#4caf50',  // Green - L5
      6: '#00bcd4',  // Cyan - L6
      7: '#2196f3',  // Blue - L7
      8: '#3f51b5',  // Indigo - L8
      9: '#9c27b0',  // Purple - L9
      10: '#673ab7'  // Deep Purple - L10 (highest)
    };
    return levelColors[level] || '#757575'; // Default gray for unknown levels
  };

  // Helper function to create employee node
  const createEmployeeNode = (employee, children) => ({
    id: employee.userId || employee._id || employee.id,
      label: (
      <Box sx={{ textAlign: 'center', p: 1.5, minWidth: 160 }}>
          <Avatar sx={{ 
            width: 40, 
            height: 40, 
            mx: 'auto', 
            mb: 1, 
            bgcolor: showDepartmentTree ? getLevelColor(employee.level) :
                   employee.role === 'Current User' ? '#1976d2' : 
                   employee.role === 'Manager' ? '#ff9800' :
                   employee.role === 'Subordinate' ? '#4caf50' :
                   employee.role === 'Direct Report' ? '#4caf50' : 
                   employee.role === 'Reporting Manager' ? '#ff9800' :
                   employee.role === 'Department Head' ? '#9c27b0' :
                   employee.role === 'Department Member' ? '#2196f3' : '#ff9800'
          }}>
            <PersonIcon />
          </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', mb: 0.5 }}>
          {employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown'}
          </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
          {employee.title || employee.jobTitle || 'Employee'} (L{employee.level})
          </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mb: 1 }}>
          {employee.department || 'General'}
          </Typography>
          <Chip 
          label={employee.role} 
            size="small" 
          color={showDepartmentTree ? 'default' : 
                 employee.role === 'Current User' ? 'primary' : 
                 employee.role === 'Manager' ? 'warning' :
                 employee.role === 'Subordinate' ? 'success' :
                 employee.role === 'Direct Report' ? 'success' : 
                 employee.role === 'Reporting Manager' ? 'warning' :
                 employee.role === 'Department Head' ? 'secondary' :
                 employee.role === 'Department Member' ? 'info' : 'warning'}
          sx={{ 
            fontSize: '0.7rem', 
            height: 20,
            bgcolor: showDepartmentTree ? getLevelColor(employee.level) : undefined,
            color: showDepartmentTree ? 'white' : undefined
          }}
          />
        </Box>
      ),
    children: children
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading team data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" size="small" onClick={fetchTeamData}>
            Retry
          </Button>
        </Box>
      </Alert>
    );
  }

  if (!teamData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No team data available.
      </Alert>
    );
  }


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom color="primary" sx={{ mb: 3 }}>
        <WorkIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        My Team
      </Typography>

      <Grid container spacing={3}>
        {/* Current User Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: '#1976d2',
                mx: 'auto',
                mb: 2
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" component="h3" gutterBottom>
              {teamData.user?.name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {teamData.user?.email || 'No email'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {teamData.user?.title || 'Employee'} • {teamData.user?.department || 'General'}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="primary">
                {teamData.directReportsCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Direct Reports
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Direct Reporting List */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Direct Reporting ({teamData.directReportsCount || 0})
            </Typography>
            
            {teamData.teamMembers && teamData.teamMembers.length > 0 ? (
              <Box sx={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: '#a8a8a8',
                  },
                },
              }}>
              <List>
                {teamData.teamMembers.map((member, index) => (
                  <React.Fragment key={`member-${member.userId}-${index}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                            <PersonIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {member.name}
                            </Typography>
                          }
                        secondary={
                          <Box>
                              <Typography variant="caption" component="span" sx={{ fontSize: '0.75rem' }}>
                              {member.email}
                            </Typography>
                              <Typography variant="caption" component="span" display="block" sx={{ fontSize: '0.75rem' }}>
                                {member.title || 'Employee'} (L{getEmployeeLevelFromITForm(member.email) || getEmployeeLevel(member.employeeGrade || member.designation || member.title)}) • {member.department || 'General'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < teamData.teamMembers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              </Box>
            ) : (
              <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                No direct reports assigned yet.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Department Members */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Department Members ({departmentMembers.length})
            </Typography>
            
            {departmentLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading department members...
                </Typography>
              </Box>
            ) : departmentMembers.length > 0 ? (
              <Box sx={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: '#a8a8a8',
                  },
                },
              }}>
              <List>
                {departmentMembers.map((member, index) => (
                  <React.Fragment key={`dept-member-${member._id}-${index}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40 }}>
                            <PersonIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {`${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown'}
                            </Typography>
                          }
                        secondary={
                          <Box>
                              <Typography variant="caption" component="span" sx={{ fontSize: '0.75rem' }}>
                              {member.email}
                            </Typography>
                              <Typography variant="caption" component="span" display="block" sx={{ fontSize: '0.75rem' }}>
                                {member.jobTitle || 'Employee'} (L{getEmployeeLevelFromITForm(member.email) || getEmployeeLevel(member.employeeGrade || member.designation || member.jobTitle)}) • {member.department || 'General'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < departmentMembers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              </Box>
            ) : (
              <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                No other employees in your department.
              </Alert>
            )}
          </Paper>
        </Grid>


        {/* Organization Tree Visualization */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {showDepartmentTree ? `Department: ${selectedTreeOption}` : 'My Reporting Chain'}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <Select
                  value={selectedTreeOption}
                  onChange={(e) => setSelectedTreeOption(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="my_chain">My Reporting Chain</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
              {(showDepartmentTree ? departmentTreeData : orgTreeData) && (
                <OrgTree
                  data={showDepartmentTree ? departmentTreeData : orgTreeData}
                  collapsable={false}
                  expandAll={true}
                  horizontal={false}
                  nodeWidth={180}
                  nodeHeight={100}
                  nodeSpacing={30}
                  levelHeight={120}
                />
              )}
              
              {/* Loading states for both trees */}
              {!showDepartmentTree && orgTreeLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading your reporting chain...
                  </Typography>
                </Box>
              )}
              
              {showDepartmentTree && departmentTreeLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading department hierarchy...
                  </Typography>
              </Box>
              )}
              
              {/* Error states for both trees */}
              {!showDepartmentTree && !orgTreeLoading && !orgTreeData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No reporting chain data available. Please ensure your manager relationships are properly set up.
                </Alert>
              )}
              
              {showDepartmentTree && !departmentTreeLoading && !departmentTreeData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No department hierarchy data available. Please ensure employees have proper manager relationships set up.
                </Alert>
              )}
            </Paper>
          </Grid>
      </Grid>

      {/* Refresh Button */}
      {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Tooltip title="Refresh team data">
          <IconButton 
            onClick={fetchTeamData}
            color="primary"
            size="large"
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box> */}
    </Box>
  );
};

export default MyTeamTab;
